import { StudyStakeholderRole, type StudyStakeholderSelectDto } from '@@types/api.types';
import { Avatar } from '@components/shared/avatar.component';
import { Tile } from '@components/shared/tile.component';
import { useGetMeiaStudyStakeholders } from '@queries/meia.query';
import { EditIcon } from 'lucide-react';
import { type FC, useEffect, useState } from 'react';
import { MeiaStakeholderModal } from './meia-stakeholder-modal.component';

type MeiaStakeholdersTileProps = {
  canManageStakeholders: boolean;
  currentUserId?: string;
  defaultCreateRole?: StudyStakeholderRole;
  onCreateStakeholderActionConsumed?: () => void;
  shouldOpenCreateStakeholder?: boolean;
  studyId: string;
};

const StakeholderCard: FC<{
  canManageStakeholders: boolean;
  currentUserId?: string;
  onEdit: () => void;
  stakeholder: StudyStakeholderSelectDto;
}> = (props) => {
  const { canManageStakeholders, currentUserId, onEdit, stakeholder } = props;
  const canEditStakeholder =
    canManageStakeholders && stakeholder.role !== StudyStakeholderRole.OWNER && stakeholder.userId !== currentUserId;
  const userName = formatStakeholderUserName(stakeholder);

  return (
    <div className="flex h-fit flex-col gap-2 rounded-md bg-gray-100 p-2">
      <Tile
        className="rounded-md p-2 shadow-none"
        contentClassName="gap-2"
        header={{
          iconButton: canEditStakeholder
            ? {
                icon: <EditIcon size={14} />,
                onClick: onEdit,
              }
            : undefined,
          title: (
            <div className="flex items-start gap-2">
              <Avatar
                altText={userName}
                imageUrl={stakeholder.user.avatarUrl}
                initialText={userName}
                size="sm"
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-700">{userName}</span>
                <span className="text-xs text-gray-500">
                  {formatStakeholderRole(stakeholder.role)} · {stakeholder.organization || 'No organization'}
                </span>
              </div>
            </div>
          ),
        }}
        hoverReveal={canEditStakeholder}
      >
        <p className="line-clamp-3 pl-10 text-xs text-gray-500">{stakeholder.responsibility || '-'}</p>
      </Tile>
    </div>
  );
};

const formatStakeholderRole = (role: StudyStakeholderRole): string => {
  switch (role) {
    case StudyStakeholderRole.DECISION_MAKER:
      return 'Decision Maker of Record';
    case StudyStakeholderRole.MISSION_ENGINEER:
      return 'Mission Engineer';
    case StudyStakeholderRole.OWNER:
      return 'Author / Lead';
    case StudyStakeholderRole.REVIEWER:
      return 'Reviewer';
    case StudyStakeholderRole.SPONSOR:
      return 'Sponsor';
  }
};

const formatStakeholderUserName = (stakeholder: StudyStakeholderSelectDto): string => {
  const profile = stakeholder.user?.profile;
  const fullName = profile ? `${profile.firstName} ${profile.lastName}`.trim() : '';
  return fullName || stakeholder.user?.email || stakeholder.userId;
};

export const MeiaStakeholdersTile: FC<MeiaStakeholdersTileProps> = (props) => {
  const {
    canManageStakeholders,
    currentUserId,
    defaultCreateRole,
    onCreateStakeholderActionConsumed,
    shouldOpenCreateStakeholder,
    studyId,
  } = props;
  const { data: stakeholders = [], isLoading } = useGetMeiaStudyStakeholders({ studyId });
  const [editingStakeholderId, setEditingStakeholderId] = useState<string>();
  const [isCreating, setIsCreating] = useState(false);

  const editingStakeholder = stakeholders.find((stakeholder) => stakeholder.id === editingStakeholderId);
  const hasStakeholders = stakeholders.length > 0;
  const canAdd = canManageStakeholders;

  const headerButton = canAdd ? { onClick: () => setIsCreating(true), text: '+ Add' } : undefined;

  const emptyState = canAdd
    ? { message: 'Add Stakeholder', onClick: () => setIsCreating(true), title: 'No Stakeholders' }
    : {
        title: 'No Stakeholders',
      };

  useEffect(() => {
    if (!shouldOpenCreateStakeholder) return;

    if (canManageStakeholders) {
      setIsCreating(true);
    }

    onCreateStakeholderActionConsumed?.();
  }, [canManageStakeholders, onCreateStakeholderActionConsumed, shouldOpenCreateStakeholder]);

  return (
    <>
      <Tile
        emptyState={emptyState}
        header={{
          button: headerButton,
          title: 'Stakeholders',
        }}
        isEmpty={!hasStakeholders}
        isLoading={isLoading}
      >
        <div className="flex flex-col gap-3">
          {stakeholders.map((stakeholder) => (
            <StakeholderCard
              canManageStakeholders={canManageStakeholders}
              currentUserId={currentUserId}
              key={stakeholder.id}
              onEdit={() => setEditingStakeholderId(stakeholder.id)}
              stakeholder={stakeholder}
            />
          ))}
        </div>
      </Tile>

      {canManageStakeholders && isCreating && (
        <MeiaStakeholderModal
          defaultRole={defaultCreateRole}
          isOpen={isCreating}
          onClose={() => setIsCreating(false)}
          studyId={studyId}
        />
      )}
      {canManageStakeholders && editingStakeholder && editingStakeholder.userId !== currentUserId && (
        <MeiaStakeholderModal
          isOpen={Boolean(editingStakeholderId)}
          onClose={() => setEditingStakeholderId(undefined)}
          stakeholder={editingStakeholder}
          studyId={studyId}
        />
      )}
    </>
  );
};
